import React, { useEffect, useState } from "react";
import { Settings, Plus, Trash2 } from "lucide-react";
import Button from "@/components/ui/Button";
import Dropdown from "@/components/ui/DropDown";
import TextField from "@/components/ui/Textfield";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import z from "zod";
import CampaignDetailWrapper from "./campaign-detail-wrapper";
import useCampaignStore from "../store/campaignStore";

// Updated Types
interface Asset {
  id: string;
  publisher: string;
  adType: string;
  fields: { [key: string]: any };
}

interface AssetGroup {
  flight_id: string;
  flight_index: number;
  language_id: string;
  language_name: string;
  objective_id: string;
  region_name: string;
  category_id: string;
  flight_row_id: string;
  flight_ad_categories_id: string;
  assets: Asset[];
}

type SimplifiedAssetState = AssetGroup[];

const AssetSetup: React.FC = () => {
  const [selectedFlight, setSelectedFlight] = useState<number>(0);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("all");
  const [selectedCategories, setSelectedCategories] = useState<{
    [regionKey: string]: string;
  }>({});

  const [assetGroups, setAssetGroups] = useState<SimplifiedAssetState>([]);

  const campaign = useCampaignStore((state) => state.campaign);
  const {
    getAdCategoryData,
    assetsPageData,
    adCategoryData,
    uiParametersByAdType,
    getAssetsPageData,
    createAssets,
    parameterDefinitions,
    assetsValues,
    updateAssets,
  } = useCampaignStore(
    useShallow((state) => ({
      getAdCategoryData: state.getAdCategoryData,
      assetsPageData: state.assetsPageData,
      adCategoryData: state.adCategoryData,
      uiParametersByAdType: state.uiParametersByAdType,
      getAssetsPageData: state.getAssetsPageData,
      createAssets: state.createAssets,
      parameterDefinitions: state.parameterDefinitions,
      assetsValues: state.assetsValues,
      updateAssets: state.updateAssets,
    }))
  );

  const { setError, showSuccess } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
      showSuccess: state.showSuccess,
    }))
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const createDefaultAsset = (): Asset => ({
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    publisher: "",
    adType: "",
    fields: {},
  });

  const findAssetGroup = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ): AssetGroup | undefined => {
    return assetGroups.find(
      (group) =>
        group.flight_index === flightIndex &&
        group.language_id === languageId &&
        group.objective_id === objectiveId &&
        group.region_name === regionName &&
        group.category_id === categoryId
    );
  };

  const getOrCreateAssetGroup = (
    flightId: string,
    flightIndex: number,
    languageId: string,
    languageName: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    flightRowId: string,
    flightAdCategoriesId: string
  ): AssetGroup => {
    const existing = findAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    );

    if (existing) {
      return existing;
    }

    const newGroup: AssetGroup = {
      flight_id: flightId,
      flight_index: flightIndex,
      language_id: languageId,
      language_name: languageName,
      objective_id: objectiveId,
      region_name: regionName,
      category_id: categoryId,
      flight_row_id: flightRowId,
      flight_ad_categories_id: flightAdCategoriesId,
      assets: [createDefaultAsset()],
    };

    setAssetGroups((prev) => [...prev, newGroup]);
    return newGroup;
  };

  const getCurrentAssets = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ): Asset[] => {
    const group = findAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    );
    return group?.assets || [];
  };

  const updateAssetGroup = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    updater: (prev: Asset[]) => Asset[]
  ) => {
    setAssetGroups((prev) =>
      prev.map((group) =>
        group.flight_index === flightIndex &&
        group.language_id === languageId &&
        group.objective_id === objectiveId &&
        group.region_name === regionName &&
        group.category_id === categoryId
          ? { ...group, assets: updater(group.assets) }
          : group
      )
    );
  };

  const addAsset = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ) => {
    updateAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId,
      (prev) => [...prev, createDefaultAsset()]
    );
  };

  const deleteAsset = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    assetId: string
  ) => {
    updateAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId,
      (prev) => {
        const filtered = prev.filter((asset) => asset.id !== assetId);
        return filtered.length === 0 ? [createDefaultAsset()] : filtered;
      }
    );
  };

  const updateAssetField = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    assetId: string,
    field: string,
    value: any
  ) => {
    updateAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId,
      (prev) =>
        prev.map((asset) =>
          asset.id === assetId ? { ...asset, [field]: value } : asset
        )
    );
  };

  const updateAssetFields = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    assetId: string,
    fields: { [key: string]: any }
  ) => {
    updateAssetGroup(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId,
      (prev) =>
        prev.map((asset) =>
          asset.id === assetId
            ? { ...asset, fields: { ...asset.fields, ...fields } }
            : asset
        )
    );
  };

  const getSelectedCategory = (regionKey: string): string => {
    return selectedCategories[regionKey] || "";
  };

  const setSelectedCategory = (regionKey: string, categoryId: string) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [regionKey]: categoryId,
    }));
  };

  const getAssetCount = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ): number => {
    return getCurrentAssets(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    ).length;
  };

  const getTableColumns = (categoryId: string, currentAssets: Asset[]) => {
    if (!uiParametersByAdType || !categoryId) return [];

    const adCategoryParams = uiParametersByAdType[categoryId];
    if (!adCategoryParams) return [];

    const columnsMap = new Map();

    currentAssets.forEach((asset) => {
      if (!asset.adType) return;

      const adTypeData = adCategoryParams.find(
        (adType) => adType.id === asset.adType
      );
      if (!adTypeData) return;

      adTypeData.parameters.forEach((param) => {
        if (param.key === "due_dates") return;

        const variant = param.variants.find(
          (v) => v.ad_type_id === asset.adType
        );

        if (
          variant &&
          variant.value_type === "user_defined" &&
          variant.fields
        ) {
          variant.fields.forEach((field) => {
            const columnKey = `${param.key}_${field.key}`;
            if (!columnsMap.has(columnKey)) {
              columnsMap.set(columnKey, {
                key: columnKey,
                label: `${param.label} - ${field.label}`,
                paramKey: param.key,
                fieldKey: field.key,
              });
            }
          });
        } else if (variant && variant.value_type === "multi_publisher") {
          if (!columnsMap.has(param.key)) {
            columnsMap.set(param.key, {
              key: param.key,
              label: param.label,
              paramKey: param.key,
              fieldKey: null,
            });
          }
        }
      });
    });

    return Array.from(columnsMap.values());
  };

  const getDueDateFields = (categoryId: string, adTypeId: string) => {
    if (!uiParametersByAdType || !categoryId || !adTypeId) {
      return [];
    }

    const adCategoryParams = uiParametersByAdType[categoryId];
    if (!adCategoryParams) return [];

    const adTypeData = adCategoryParams.find(
      (adType) => adType.id === adTypeId
    );
    if (!adTypeData) return [];

    const dueDatesParam = adTypeData.parameters.find(
      (param) => param.key === "due_dates"
    );

    if (!dueDatesParam) return [];

    const variant = dueDatesParam.variants.find(
      (v) => v.ad_type_id === adTypeId
    );

    if (!variant || !variant.fields) return [];

    return variant.fields.map((field) => ({
      key: `${dueDatesParam.key}_${field.key}`,
      label: field.label,
      type: field.type,
      paramKey: dueDatesParam.key,
      fieldKey: field.key,
      definitionId: dueDatesParam.definition_id,
    }));
  };

  const renderAssetTable = (
    flightIndex: number,
    languageId: string,
    objectiveId: string,
    regionName: string,
    categoryId: string
  ) => {
    const selectedCategoryData = adCategoryData.find(
      (cat) => cat.ad_category_id === categoryId
    );

    if (!selectedCategoryData) return null;

    const currentAssets = getCurrentAssets(
      flightIndex,
      languageId,
      objectiveId,
      regionName,
      categoryId
    );
    const tableColumns = getTableColumns(categoryId, currentAssets);

    const getInputClasses = () => "w-full px-3 py-2 min-w-[150px]";

    const headerDueDateFields =
      currentAssets.length > 0 && currentAssets[0].adType
        ? getDueDateFields(categoryId, currentAssets[0].adType)
        : [];

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left relative">
          <thead className="bg-gray-100 text-gray-700 font-medium">
            <tr>
              <th className="py-3 px-4 min-w-[150px]">Publisher</th>
              <th className="py-3 px-4 min-w-[150px]">Ad Type</th>
              {tableColumns
                .filter((column) => !column.paramKey.includes("due_dates"))
                .map((column) => (
                  <th key={column.key} className="py-3 px-4 min-w-[150px]">
                    {column.label}
                  </th>
                ))}
              {headerDueDateFields.length > 0 ? (
                <>
                  <th className="px-3 rounded sticky right-0 bg-gray-200 z-20 min-w-[220px]">
                    <div className="flex items-center justify-between font-medium text-gray-600 space-x-1">
                      {headerDueDateFields.map((field) => (
                        <span key={field.key} className="w-[120px]">
                          {field.label}
                        </span>
                      ))}
                      <span className="w-[20px] text-center"></span>
                    </div>
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {currentAssets.map((asset) => {
              const dueDateFields = getDueDateFields(categoryId, asset.adType);

              const renderPublisherField = () => {
                const publisher = selectedCategoryData.publishers?.[0];
                if (publisher?.type === "single") {
                  if (asset.publisher !== publisher.id) {
                    updateAssetField(
                      flightIndex,
                      languageId,
                      objectiveId,
                      regionName,
                      categoryId,
                      asset.id,
                      "publisher",
                      publisher.id
                    );
                  }

                  return (
                    <TextField
                      type="text"
                      value={publisher.name}
                      disabled
                      className={`${getInputClasses()} bg-gray-100`}
                    />
                  );
                } else if (publisher?.type === "multi") {
                  return (
                    <Dropdown
                      options={selectedCategoryData.publishers.map((pub) => ({
                        title: pub.name,
                        value: pub.id,
                      }))}
                      selected={asset.publisher}
                      setSelected={(value) =>
                        updateAssetField(
                          flightIndex,
                          languageId,
                          objectiveId,
                          regionName,
                          categoryId,
                          asset.id,
                          "publisher",
                          value
                        )
                      }
                    />
                  );
                } else {
                  return (
                    <TextField
                      type="text"
                      placeholder="Add publisher"
                      value={asset.publisher}
                      onChange={(e) =>
                        updateAssetField(
                          flightIndex,
                          languageId,
                          objectiveId,
                          regionName,
                          categoryId,
                          asset.id,
                          "publisher",
                          e.target.value
                        )
                      }
                      className={getInputClasses()}
                    />
                  );
                }
              };

              const renderAdTypeField = () => {
                let currentPublisherId = asset.publisher;

                if (
                  !currentPublisherId &&
                  selectedCategoryData.publishers?.[0]?.type === "single"
                ) {
                  currentPublisherId = selectedCategoryData.publishers[0]?.id;
                }

                // The following changes were made to handle user-defined publishers
                const filteredAdTypes =
                  selectedCategoryData.ad_types.filter(
                    (adType) => adType.publisher_id === currentPublisherId
                  ) || [];

                // ✅ fallback for user-defined publisher
                const isUserDefinedPublisher =
                  !selectedCategoryData.publishers.some(
                    (pub) => pub.id === currentPublisherId
                  );

                if (isUserDefinedPublisher) {
                  // show all ad types if user-defined publisher
                  filteredAdTypes.push(...selectedCategoryData.ad_types);
                }

                // End changes

                if (!currentPublisherId || !filteredAdTypes.length) {
                  return (
                    <Dropdown
                      options={[]}
                      selected=""
                      disabled
                      placeholder="Select Publisher"
                      setSelected={(e) => {}}
                    />
                  );
                }

                return (
                  <Dropdown
                    options={filteredAdTypes.map((adType) => ({
                      title: adType.name,
                      value: adType.id,
                    }))}
                    selected={asset.adType}
                    setSelected={(value) => {
                      updateAssetField(
                        flightIndex,
                        languageId,
                        objectiveId,
                        regionName,
                        categoryId,
                        asset.id,
                        "adType",
                        value
                      );
                      updateAssetField(
                        flightIndex,
                        languageId,
                        objectiveId,
                        regionName,
                        categoryId,
                        asset.id,
                        "fields",
                        {}
                      );
                    }}
                  />
                );
              };

              const renderDynamicFields = () => {
                if (!uiParametersByAdType || !asset.adType || !categoryId) {
                  return tableColumns
                    .filter((column) => !column.paramKey.includes("due_dates"))
                    .map((column) => (
                      <td key={column.key} className="py-3 px-4" />
                    ));
                }

                const adCategoryParams = uiParametersByAdType[categoryId];
                if (!adCategoryParams) {
                  return tableColumns
                    .filter((column) => !column.paramKey.includes("due_dates"))
                    .map((column) => (
                      <td key={column.key} className="py-3 px-4" />
                    ));
                }

                const adTypeData = adCategoryParams.find(
                  (adType) => adType.id === asset.adType
                );
                if (!adTypeData) {
                  return tableColumns
                    .filter((column) => !column.paramKey.includes("due_dates"))
                    .map((column) => (
                      <td key={column.key} className="py-3 px-4" />
                    ));
                }

                return tableColumns
                  .filter((column) => !column.paramKey.includes("due_dates"))
                  .map((column) => {
                    const param = adTypeData.parameters.find(
                      (p) => p.key === column.paramKey
                    );
                    if (!param) {
                      return <td key={column.key} className="py-3 px-4" />;
                    }

                    if (param.key === "due_dates") {
                      return <td key={column.key} className="py-3 px-4" />;
                    }

                    const variant = param.variants.find(
                      (v) => v.ad_type_id === asset.adType
                    );
                    if (!variant) {
                      return <td key={column.key} className="py-3 px-4" />;
                    }

                    if (variant.value_type === "user_defined") {
                      const field = variant.fields.find(
                        (f) => `${param.key}_${f.key}` === column.key
                      );

                      if (!field) {
                        return <td key={column.key} className="py-3 px-4" />;
                      }

                      const fieldKey = column.key;

                      if (
                        field.type === "select" ||
                        field.type === "dropdown"
                      ) {
                        const foundField = parameterDefinitions[
                          variant.media_type
                        ]
                          ?.flatMap((item) => item.fields)
                          ?.find((e) => e.id === field.parameter_field_id);
                        const fieldOptions = Array.isArray(foundField?.options)
                          ? foundField.options
                          : [];

                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <Dropdown
                              options={fieldOptions.map((opt: any) => ({
                                title:
                                  opt.title ||
                                  opt.label ||
                                  opt.name ||
                                  String(opt),
                                value:
                                  opt.value ||
                                  opt.id ||
                                  opt.title ||
                                  String(opt),
                              }))}
                              selected={asset.fields?.[fieldKey] || ""}
                              setSelected={(value) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: value,
                                  }
                                );
                              }}
                            />
                          </td>
                        );
                      } else if (field.type === "number") {
                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <TextField
                              type="number"
                              placeholder={field.label}
                              value={asset.fields?.[fieldKey] || ""}
                              onChange={(e) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: e.target.value,
                                  }
                                );
                              }}
                              className={getInputClasses()}
                            />
                          </td>
                        );
                      } else if (field.type === "date") {
                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <TextField
                              type="date"
                              value={asset.fields?.[fieldKey] || ""}
                              onChange={(e) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: e.target.value,
                                  }
                                );
                              }}
                              className={getInputClasses()}
                            />
                          </td>
                        );
                      } else if (field.type === "list|text") {
                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <TextField
                              type="text"
                              placeholder={field.label}
                              value={asset.fields?.[fieldKey] || ""}
                              onChange={(e) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: e.target.value,
                                  }
                                );
                              }}
                              className={getInputClasses()}
                            />
                          </td>
                        );
                      } else {
                        return (
                          <td key={fieldKey} className="py-3 px-4">
                            <TextField
                              type="text"
                              placeholder={field.label}
                              value={asset.fields?.[fieldKey] || ""}
                              onChange={(e) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [fieldKey]: e.target.value,
                                  }
                                );
                              }}
                              className={getInputClasses()}
                            />
                          </td>
                        );
                      }
                    } else if (variant.value_type === "single_publisher") {
                      return <td key={column.key} className="py-3 px-4" />;
                    } else if (variant.value_type === "multi_publisher") {
                      let options: Array<{ title: string; value: string }> = [];
                      const fields = variant.fields;

                      if (fields && fields.length > 0) {
                        const firstFieldWithArray = fields.find((field) =>
                          Array.isArray(field.value)
                        );

                        if (
                          firstFieldWithArray &&
                          Array.isArray(firstFieldWithArray.value)
                        ) {
                          const arrayLength = firstFieldWithArray.value.length;

                          options = Array.from({ length: arrayLength }).map(
                            (_, i) => {
                              const entryParts: string[] = [];
                              const valueParts: string[] = [];

                              fields.forEach((field) => {
                                if (
                                  Array.isArray(field.value) &&
                                  field.value[i] !== undefined
                                ) {
                                  entryParts.push(
                                    `${field.label}: ${field.value[i]}`
                                  );
                                  valueParts.push(String(field.value[i]));
                                }
                              });

                              return {
                                title: entryParts.join(" - "),
                                value: valueParts.join("_"),
                              };
                            }
                          );
                        }
                      }

                      return (
                        <td key={column.key} className="py-3 px-4">
                          <Dropdown
                            options={options}
                            selected={asset.fields?.[column.paramKey] || ""}
                            setSelected={(value) => {
                              updateAssetFields(
                                flightIndex,
                                languageId,
                                objectiveId,
                                regionName,
                                categoryId,
                                asset.id,
                                {
                                  [column.paramKey]: value,
                                }
                              );
                            }}
                          />
                        </td>
                      );
                    }

                    return <td key={column.key} className="py-3 px-4" />;
                  });
              };

              return (
                <tr key={asset.id} className="border-t border-gray-100">
                  <td className="py-3 px-4">{renderPublisherField()}</td>
                  <td className="py-3 px-4">{renderAdTypeField()}</td>
                  {renderDynamicFields()}

                  {dueDateFields.length > 0 ? (
                    <>
                      <td className="px-3 sticky right-0 bg-gray-200 z-10 min-w-[220px] rounded">
                        <div className="flex items-center space-x-1">
                          {dueDateFields.map((dueDateField) => (
                            <TextField
                              key={dueDateField.key}
                              type={
                                dueDateField.type === "date" ? "date" : "text"
                              }
                              placeholder={dueDateField.label}
                              value={asset.fields?.[dueDateField.key] || ""}
                              onChange={(e) => {
                                updateAssetFields(
                                  flightIndex,
                                  languageId,
                                  objectiveId,
                                  regionName,
                                  categoryId,
                                  asset.id,
                                  {
                                    [dueDateField.key]: e.target.value,
                                  }
                                );
                              }}
                              className="text-xs px-2 py-1 w-[120px]"
                            />
                          ))}
                          <button
                            onClick={() =>
                              deleteAsset(
                                flightIndex,
                                languageId,
                                objectiveId,
                                regionName,
                                categoryId,
                                asset.id
                              )
                            }
                            className="w-6 h-6 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full flex items-center justify-center"
                            title="Delete row"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Monil's Code for converting to output JSON
  const convertToOutputJSON = () => {
    const outputData: any[] = [];
    const flightMap = new Map<string, any>();

    assetGroups.forEach((group) => {
      if (!flightMap.has(group.flight_id)) {
        flightMap.set(group.flight_id, {
          flight_id: group.flight_id,
          flight_rows: [],
        });
      }

      const flightOutput = flightMap.get(group.flight_id);

      // Find existing flight_row or create new one
      let flightRow = flightOutput.flight_rows.find(
        (row: any) => row.flight_row_id === group.flight_row_id
      );

      if (!flightRow) {
        flightRow = {
          flight_row_id: group.flight_row_id,
          flight_ad_categories: [],
        };
        flightOutput.flight_rows.push(flightRow);
      }

      const flightAdCategory: any = {
        flight_ad_categories_id: group.flight_ad_categories_id,
        ad_rows: [],
      };

      // Get version count from adCategoryData
      const categoryData = adCategoryData.find(
        (cat) => cat.ad_category_id === group.category_id
      );

      // Convert each asset to ad_row
      group.assets.forEach((asset) => {
        // Skip incomplete assets
        if (!asset.adType || !asset.publisher) return;

        // Check if this asset exists in assetsValues (existing asset)
        const isExistingAsset =
          assetsValues?.[group.category_id]?.ad_types?.[asset.adType]
            ?.publishers?.[asset.publisher]?.asset_meta?.assets_row_id ===
          asset.id;

        const adRow: any = {
          ad_row_id: asset.id,
          parameters: [],
        };

        // Only add these fields for NEW assets (not existing ones)
        if (!isExistingAsset) {
          adRow.versions = categoryData?.versions || 1;
          adRow.ad_type_id = asset.adType;
          adRow.ad_category_id = group.category_id;
        }

        // Always send publisher fields for all assets
        const knownPublisherIds =
          categoryData?.publishers.map((p) => p.id) || [];
        const isUserDefinedPublisher = !knownPublisherIds.includes(
          asset.publisher
        );

        if (isUserDefinedPublisher) {
          adRow.is_publisher_user_defined = true;
          adRow.publisher_name = asset.publisher;
          adRow.publisher_id = null;
        } else {
          adRow.is_publisher_user_defined = false;
          adRow.publisher_id = asset.publisher;
          adRow.publisher_name =
            categoryData?.publishers.find((p) => p.id === asset.publisher)
              ?.name || "";
        }

        const adCategoryParams = uiParametersByAdType?.[group.category_id];
        const adTypeParams = adCategoryParams?.find(
          (adType) => adType.id === asset.adType
        );

        if (adTypeParams) {
          // Process all parameters including single_publisher
          adTypeParams.parameters.forEach((param) => {
            const variant = param.variants.find(
              (v) => v.ad_type_id === asset.adType
            );

            if (!variant) return;

            if (variant.value_type === "single_publisher") {
              // Handle single_publisher: Add all fields from variant
              if (variant.fields && variant.fields.length > 0) {
                variant.fields.forEach((field) => {
                  if (field.value !== null && field.value !== undefined) {
                    adRow.parameters.push({
                      parameter_definition_id: param.definition_id,
                      parameter_key: field.key,
                      parameter_value: field.value.toString(),
                    });
                  }
                });
              }
            } else if (variant.value_type === "user_defined") {
              // Handle user_defined: Get values from asset.fields
              if (variant.fields && variant.fields.length > 0) {
                variant.fields.forEach((field) => {
                  const fieldKey = `${param.key}_${field.key}`;
                  const fieldValue = asset.fields?.[fieldKey];

                  if (
                    fieldValue !== null &&
                    fieldValue !== undefined &&
                    fieldValue !== ""
                  ) {
                    adRow.parameters.push({
                      parameter_definition_id: param.definition_id,
                      parameter_key: field.key,
                      parameter_value: fieldValue,
                    });
                  }
                });
              }
            } else if (variant.value_type === "multi_publisher") {
              // Handle multi_publisher: Get value from asset.fields
              const fieldValue = asset.fields?.[param.key];

              if (
                fieldValue !== null &&
                fieldValue !== undefined &&
                fieldValue !== ""
              ) {
                adRow.parameters.push({
                  parameter_definition_id: param.definition_id,
                  parameter_key: param.key,
                  parameter_value: fieldValue,
                });
              }
            }
          });
        }

        if (asset.publisher && asset.adType) {
          flightAdCategory.ad_rows.push(adRow);
        }
      });

      if (flightAdCategory.ad_rows.length > 0) {
        flightRow.flight_ad_categories.push(flightAdCategory);
      }
    });

    // Convert map to array
    flightMap.forEach((flight) => {
      if (flight.flight_rows.length > 0) {
        outputData.push(flight);
      }
    });

    return {
      campaign_id: searchParams.get("id") || "",
      data: outputData,
    };
  };

  const convertExistingAssetsToLocalState = (
    existingAssets: any,
    flightId: string,
    flightIndex: number,
    languageId: string,
    languageName: string,
    objectiveId: string,
    regionName: string,
    categoryId: string,
    flightRowId: string,
    flightAdCategoriesId: string
  ): Asset[] => {
    if (!existingAssets || !existingAssets[categoryId]) {
      return [];
    }

    const categoryData = existingAssets[categoryId];
    const assets: Asset[] = [];

    Object.keys(categoryData.ad_types).forEach((adTypeId) => {
      const adTypeData = categoryData.ad_types[adTypeId];

      Object.keys(adTypeData.publishers).forEach((publisherId) => {
        const publisherData = adTypeData.publishers[publisherId];
        const assetMeta = publisherData.asset_meta;

        // Match by flight_row_id and ad_category_id (more stable than flight_ad_categories_id)
        if (
          assetMeta.flight_row_id === flightRowId &&
          assetMeta.ad_category_id === categoryId
        ) {
          const fields: { [key: string]: any } = {};

          // Process parameters from parameter_value_pair
          if (
            assetMeta.parameter_value_pair &&
            assetMeta.parameter_value_pair.parameters
          ) {
            assetMeta.parameter_value_pair.parameters.forEach((param: any) => {
              const adCategoryParams = uiParametersByAdType?.[categoryId];
              if (adCategoryParams) {
                const adTypeParams = adCategoryParams.find(
                  (at) => at.id === adTypeId
                );
                if (adTypeParams) {
                  const paramDef = adTypeParams.parameters.find(
                    (p) => p.definition_id === param.definition_id
                  );

                  if (paramDef) {
                    const variant = paramDef.variants.find(
                      (v) => v.ad_type_id === adTypeId
                    );

                    if (variant && variant.value_type === "user_defined") {
                      const fieldKey = `${paramDef.key}_${param.key}`;
                      fields[fieldKey] = param.value;
                    } else if (
                      variant &&
                      variant.value_type === "multi_publisher"
                    ) {
                      fields[paramDef.key] = param.value;
                    }
                  } else {
                    fields[param.key] = param.value;
                  }
                }
              }
            });
          }

          assets.push({
            id: assetMeta.assets_row_id,
            publisher: publisherId,
            adType: adTypeId,
            fields: fields,
          });
        }
      });
    });

    return assets;
  };

  const saveData = async () => {
    try {
      const outputJSON = convertToOutputJSON();

      if (assetsValues) {
        await updateAssets(outputJSON);
        showSuccess("Assets Updated Successfully");
      } else {
        await createAssets(outputJSON);
        showSuccess("Assets created Successfully");
      }

      navigate(`/campaign/summary?id=${searchParams.get("id")}`);
    } catch (err: unknown) {
      if (err instanceof z.ZodError) {
        err.issues.forEach((issue) => {
          console.warn(
            `Field: ${issue.path.join(".")}, Error: ${issue.message}`
          );
        });

        const firstIssue = err.issues[0];
        setError(firstIssue?.message || "Validation failed");
      } else {
        setError((err as Error).message || "Something went wrong");
      }
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const campaignId = searchParams.get("id");
      if (campaignId) {
        await getAssetsPageData(campaignId);
        await getAdCategoryData(campaignId);
      }
    } catch (error: any) {
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (assetsValues && assetsPageData && assetsPageData.length > 0) {
      const newAssetGroups: AssetGroup[] = [];

      assetsPageData.forEach((flight, flightIndex) => {
        flight.languages.forEach((language) => {
          language.objectives.forEach((objective) => {
            objective.locations.forEach((location) => {
              const flightRowsObj = location.flight_rows;
              if (!flightRowsObj) return;

              const flightRowIds = Object.keys(flightRowsObj);
              if (flightRowIds.length === 0) return;

              const firstFlightRowId = flightRowIds[0];
              const flightRowData = flightRowsObj[firstFlightRowId];
              const adCategories = flightRowData?.ad_categories || [];

              adCategories.forEach((adCategory: any) => {
                const existingAssets = convertExistingAssetsToLocalState(
                  assetsValues,
                  flight.flight_id,
                  flightIndex,
                  language.language_id,
                  language.language_name,
                  objective.objective_id,
                  location.region,
                  adCategory.ad_category_id,
                  firstFlightRowId,
                  adCategory.flight_ad_categories_id
                );

                if (existingAssets.length > 0) {
                  newAssetGroups.push({
                    flight_id: flight.flight_id,
                    flight_index: flightIndex,
                    language_id: language.language_id,
                    language_name: language.language_name,
                    objective_id: objective.objective_id,
                    region_name: location.region,
                    category_id: adCategory.ad_category_id,
                    flight_row_id: firstFlightRowId,
                    flight_ad_categories_id: adCategory.flight_ad_categories_id,
                    assets: existingAssets,
                  });
                }
              });
            });
          });
        });
      });

      if (newAssetGroups.length > 0) {
        setAssetGroups(newAssetGroups);
      }
    }
  }, [assetsValues, assetsPageData, uiParametersByAdType]);

  useEffect(() => {
    if (
      assetsPageData &&
      assetsPageData.length > 0 &&
      assetsPageData[selectedFlight]?.languages.length > 0 &&
      selectedLanguage === ""
    ) {
      setSelectedLanguage("all");
    }
  }, [assetsPageData, selectedFlight]);

  if (!campaign) {
    return <Navigate to="/dashboard" />;
  }

  const languagesToDisplay =
    selectedLanguage === "all"
      ? assetsPageData?.[selectedFlight]?.languages || []
      : assetsPageData?.[selectedFlight]?.languages.filter(
          (lang) => lang.language_id === selectedLanguage
        ) || [];

  return (
    <CampaignDetailWrapper>
      <div className="mb-8">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex-1 min-w-0 overflow-x-auto pr-5 scrollbar-hidden">
            <div className="flex w-max space-x-0">
              {assetsPageData &&
                assetsPageData.length > 0 &&
                assetsPageData.map((flight, index) => (
                  <button
                    key={flight.flight_number}
                    onClick={() => setSelectedFlight(index)}
                    className={`px-10 py-2 font-medium transition-colors border ${
                      selectedFlight === index
                        ? "bg-[#EA33C0] text-white border-[#EA33C0]"
                        : "bg-white text-gray-700 border-[#EA33C0] hover:bg-pink-50"
                    } ${
                      index === 0
                        ? "rounded-l-full"
                        : index === assetsPageData.length - 1
                        ? "rounded-r-full border-l-0"
                        : "border-l-0"
                    }`}
                  >
                    Flight {index + 1}
                  </button>
                ))}
            </div>
          </div>

          <div className="flex items-center space-x-2 shrink-0">
            <span className="text-gray-700">Language</span>
            <Dropdown
              options={[
                { title: "All Languages", value: "all" },
                ...(assetsPageData?.[selectedFlight]?.languages.map((lang) => ({
                  title: lang.language_name,
                  value: lang.language_id,
                })) || []),
              ]}
              selected={selectedLanguage}
              setSelected={setSelectedLanguage}
              className="min-w-36"
            />
          </div>

          <Button
            type="secondary"
            leadingIcon={<Settings className="w-4 h-4" />}
            className="w-auto shrink-0"
            onClick={() => {
              navigate(`/campaign/flight?id=${searchParams.get("id")}`);
            }}
          >
            Edit Flights
          </Button>
        </div>
      </div>

      {isLoading && <div className="text-center py-8">Loading...</div>}

      {!isLoading && (!assetsPageData || assetsPageData.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          No assets data available. Please create flights first.
        </div>
      )}

      {!isLoading &&
        languagesToDisplay.length > 0 &&
        languagesToDisplay.map((currentLanguageData) => (
          <div key={currentLanguageData.language_id}>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {currentLanguageData.language_name} Assets
            </h3>

            <hr className="border-gray-300 mb-5" />

            {currentLanguageData.objectives.map((objective) =>
              objective.locations.map((location) => {
                const regionKey = `${objective.objective_id}-${location.region}`;
                const flightData = assetsPageData[selectedFlight];

                // Get flight_rows object
                const flightRowsObj = location.flight_rows;
                if (!flightRowsObj) return null;

                // Get first flight_row_id
                const flightRowIds = Object.keys(flightRowsObj);
                if (flightRowIds.length === 0) return null;

                const firstFlightRowId = flightRowIds[0];
                const flightRowData = flightRowsObj[firstFlightRowId];

                // Get ad_categories from the flight_row
                const adCategories = flightRowData?.ad_categories || [];

                // Get first category as default if not selected
                const selectedCategoryId =
                  getSelectedCategory(regionKey) ||
                  adCategories[0]?.ad_category_id ||
                  "";

                // Find the flight_ad_categories_id for the selected category
                const selectedCategoryInfo = adCategories.find(
                  (cat: any) => cat.ad_category_id === selectedCategoryId
                );

                // Initialize or get asset group
                if (selectedCategoryId && selectedCategoryInfo) {
                  getOrCreateAssetGroup(
                    flightData.flight_id,
                    selectedFlight,
                    currentLanguageData.language_id,
                    currentLanguageData.language_name,
                    objective.objective_id,
                    location.region,
                    selectedCategoryId,
                    firstFlightRowId,
                    selectedCategoryInfo.flight_ad_categories_id
                  );

                  // Set selected category if not set
                  if (!getSelectedCategory(regionKey)) {
                    setSelectedCategory(regionKey, selectedCategoryId);
                  }
                }

                return (
                  <div key={regionKey} className="mb-8">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">
                          {objective.objective_name} / {location.region}
                        </h4>
                        <p className="text-gray-600">
                          {adCategories.length} Assets
                        </p>
                      </div>

                      <div className="flex space-x-2">
                        {adCategories.map((cat: any) => {
                          const assetCount = getAssetCount(
                            selectedFlight,
                            currentLanguageData.language_id,
                            objective.objective_id,
                            location.region,
                            cat.ad_category_id
                          );

                          return (
                            <button
                              key={cat.ad_category_id}
                              onClick={() => {
                                setSelectedCategory(
                                  regionKey,
                                  cat.ad_category_id
                                );
                                getOrCreateAssetGroup(
                                  flightData.flight_id,
                                  selectedFlight,
                                  currentLanguageData.language_id,
                                  currentLanguageData.language_name,
                                  objective.objective_id,
                                  location.region,
                                  cat.ad_category_id,
                                  firstFlightRowId,
                                  cat.flight_ad_categories_id
                                );
                              }}
                              className={`border inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                getSelectedCategory(regionKey) ===
                                cat.ad_category_id
                                  ? "bg-gray-700 text-white"
                                  : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {cat.ad_category_name}
                              <span className="ml-4 text-xs">{assetCount}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <hr className="border-gray-300 mb-4" />

                    {selectedCategoryId && (
                      <>
                        {renderAssetTable(
                          selectedFlight,
                          currentLanguageData.language_id,
                          objective.objective_id,
                          location.region,
                          selectedCategoryId
                        )}

                        <Button
                          type="secondary"
                          leadingIcon={<Plus className="w-4 h-4" />}
                          onClick={() =>
                            addAsset(
                              selectedFlight,
                              currentLanguageData.language_id,
                              objective.objective_id,
                              location.region,
                              selectedCategoryId
                            )
                          }
                          className="mt-4"
                        >
                          Add Row
                        </Button>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ))}

      <Button type="primary" onClick={saveData}>
        Save Changes
      </Button>
    </CampaignDetailWrapper>
  );
};

export default AssetSetup;
